// The boat's boards for the shell (TOM-192): read once per boat, and again
// whenever a board changes — a `board_changed` frame from any device, or a
// change made on this page (NEO_BOARD_UPDATED_EVENT) — and when the page
// becomes visible. A failed refresh keeps the list already shown, marked stale.
//
// Your boards' editing state writes through here, so the sidebar and the bar
// follow at once: a reorder against the list's `orderVersion`, shown before the
// server answers, and a board's removal.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { NEO_BOARD_UPDATED_EVENT, withBoardKinds } from './neoBoard';
import { parseNeoBoardList, type NeoBoardSummary } from './neoBoards';

export type NeoBoardsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      /** The owner's boards, in the boat's order. */
      boards: NeoBoardSummary[];
      /** The system boards, in their fixed order (TOM-358). */
      systemBoards: NeoBoardSummary[];
      orderVersion: number;
      /** The last refresh failed: this is the list as it was at `readAt`. */
      stale: boolean;
      readAt: number;
    };

/**
 * How a change to the list went: saved; refused because the list changed on
 * another screen first; the board was already gone; or not saved.
 */
export type BoardsWriteOutcome = 'ok' | 'conflict' | 'gone' | 'failed';

export interface NeoBoardsControls {
  state: NeoBoardsState;
  refresh: () => Promise<void>;
  /** Put the boards in this order: every board's slug, once. */
  reorder: (order: string[]) => Promise<BoardsWriteOutcome>;
  /** Remove a board and everything on it, for every owner. */
  remove: (slug: string) => Promise<BoardsWriteOutcome>;
}

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/** `changeSeq` moves with every `board_changed` frame for the boat (BoatChrome.boardChanged). */
export function useNeoBoards(boatId: string, changeSeq: number): NeoBoardsControls {
  const [state, setState] = useState<NeoBoardsState>({ kind: 'loading' });
  const request = useRef(0);
  const latest = useRef<NeoBoardsState>(state);
  latest.current = state;

  const refresh = useCallback(async () => {
    const mine = ++request.current;
    try {
      const list = parseNeoBoardList(await api(withBoardKinds(`/v1/boats/${boatId}/boards`)));
      if (mine !== request.current) return;
      if (list === null) throw new Error('unreadable list');
      setState({
        kind: 'ready',
        boards: list.boards,
        systemBoards: list.systemBoards,
        orderVersion: list.orderVersion,
        stale: false,
        readAt: Date.now(),
      });
    } catch {
      if (mine !== request.current) return;
      setState((prev) => (prev.kind === 'ready' ? { ...prev, stale: true } : { kind: 'error' }));
    }
  }, [boatId]);

  useEffect(() => {
    setState({ kind: 'loading' });
    void refresh();
    const onVisible = () => {
      if (!document.hidden) void refresh();
    };
    const onChanged = () => void refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(NEO_BOARD_UPDATED_EVENT, onChanged);
    return () => {
      request.current += 1;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NEO_BOARD_UPDATED_EVENT, onChanged);
    };
  }, [refresh]);

  // A board changed somewhere: on this device, the app, or a co-owner's.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    void refresh();
  }, [changeSeq, refresh]);

  const reorder = useCallback(
    async (order: string[]): Promise<BoardsWriteOutcome> => {
      const list = latest.current;
      if (list.kind !== 'ready') return 'failed';
      const bySlug = new Map(list.boards.map((b) => [b.slug, b]));
      const boards = order
        .map((slug) => bySlug.get(slug))
        .filter((b): b is NeoBoardSummary => b !== undefined)
        .map((b, position) => ({ ...b, position }));
      // Show the new order at once; the server's answer replaces it.
      request.current += 1;
      setState({ ...list, boards });
      try {
        const saved = parseNeoBoardList(
          await api(withBoardKinds(`/v1/boats/${boatId}/boards`), {
            method: 'PATCH',
            body: { expectedVersion: list.orderVersion, order },
            idempotencyKey: crypto.randomUUID(),
          })
        );
        if (saved !== null) {
          request.current += 1;
          setState({
            kind: 'ready',
            boards: saved.boards,
            systemBoards: saved.systemBoards,
            orderVersion: saved.orderVersion,
            stale: false,
            readAt: Date.now(),
          });
        }
        return 'ok';
      } catch (err) {
        await refresh();
        return statusOf(err) === 409 ? 'conflict' : 'failed';
      }
    },
    [boatId, refresh]
  );

  const remove = useCallback(
    async (slug: string): Promise<BoardsWriteOutcome> => {
      try {
        await api(`/v1/boats/${boatId}/boards/${encodeURIComponent(slug)}`, {
          method: 'DELETE',
          idempotencyKey: crypto.randomUUID(),
        });
        await refresh();
        return 'ok';
      } catch (err) {
        await refresh();
        return statusOf(err) === 404 ? 'gone' : 'failed';
      }
    },
    [boatId, refresh]
  );

  return { state, refresh, reorder, remove };
}
